(() => {
  if (window.SnuglerPolish || !document.body.classList.contains('snugler-polish')) return;
  if (!('IntersectionObserver' in window) || !Element.prototype.animate) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const mobile = window.matchMedia('(max-width: 749px)');
  const sections = new Map();
  const revealed = new WeakSet();
  const listeners = new AbortController();
  const easing = 'cubic-bezier(.22, .61, .36, 1)';
  const selectors = '.rich-text__heading, .rich-text__text, .multirow .image-with-text__media, .multirow .image-with-text__content, .snr__heading, .snr__overview';

  function animate(element, state, frames, options) {
    if (reducedMotion.matches || !element.isConnected || element.closest('[hidden]')) return;
    const animation = element.animate(frames, { easing, ...options });
    state.animations.add(animation);
    const release = () => state.animations.delete(animation);
    animation.finished.then(release, release);
  }

  function reveal(element, state, stagger = 0) {
    const isImage = element.matches('.image-with-text__media');
    const isVideo = element.matches('.slidev8');
    const compact = mobile.matches;
    const distance = compact ? 4 : isImage || isVideo ? 6 : 10;
    const opacity = compact ? 0.88 : isImage || isVideo ? 0.82 : 0.72;
    const delay = compact ? Math.min(stagger, 40) : stagger;
    animate(element, state, [
      { opacity, transform: `translateY(${distance}px)` },
      { opacity: 1, transform: 'translateY(0)' },
    ], { duration: compact ? 460 : isImage ? 620 : 560, delay });
  }

  function watchGallery(gallery, state) {
    let active = gallery.querySelector('.product__media-item.is-active');
    let pending = new AbortController();
    const observer = new MutationObserver(() => {
      const next = gallery.querySelector('.product__media-item.is-active');
      if (!next || next === active) return;
      active = next;
      pending.abort();
      pending = new AbortController();
      if (!desktop.matches || reducedMotion.matches) return;
      const image = next.querySelector('.product__modal-opener--image img');
      if (!image) return;
      const fade = () => {
        if (active !== next || !next.classList.contains('is-active') || !image.naturalWidth || !desktop.matches) return;
        animate(image, state, [{ opacity: 0.86 }, { opacity: 1 }], { duration: 440 });
      };
      if (image.complete && image.naturalWidth) fade();
      else image.addEventListener('load', fade, { once: true, signal: pending.signal });
    });
    observer.observe(gallery, { attributes: true, attributeFilter: ['class'], childList: true, subtree: true });
    state.cleanup.push(() => {
      observer.disconnect();
      pending.abort();
    });
  }

  function connect(section) {
    if (sections.has(section) || reducedMotion.matches) return;
    const state = { animations: new Set(), cleanup: [], observer: null };
    sections.set(section, state);
    const candidates = new Map();
    section.querySelectorAll(selectors).forEach((element) => {
      const stagger = element.matches('.rich-text__text, .image-with-text__content, .snr__overview') ? 60 : 0;
      candidates.set(element, stagger);
    });
    section.querySelectorAll('.slider-containerv8').forEach((slider) => {
      const bounds = slider.getBoundingClientRect();
      const left = Math.max(0, bounds.left);
      const right = Math.min(window.innerWidth, bounds.right);
      let index = 0;
      slider.querySelectorAll('.slidev8').forEach((card) => {
        const rect = card.getBoundingClientRect();
        if (rect.right <= left || rect.left >= right || index >= (mobile.matches ? 1 : 3)) return;
        candidates.set(card, index * 60);
        index += 1;
      });
    });
    const pending = new Set();
    state.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        state.observer.unobserve(entry.target);
        pending.delete(entry.target);
        revealed.add(entry.target);
        reveal(entry.target, state, candidates.get(entry.target));
      });
      if (!pending.size) state.observer.disconnect();
    }, { threshold: 0.08, rootMargin: '0px 0px -24px 0px' });
    candidates.forEach((stagger, element) => {
      const rect = element.getBoundingClientRect();
      if (revealed.has(element) || !rect.width || !rect.height || element.closest('[hidden]')) return;
      if (rect.top < window.innerHeight) {
        revealed.add(element);
        return;
      }
      pending.add(element);
      state.observer.observe(element);
    });
    section.querySelectorAll('media-gallery').forEach((gallery) => watchGallery(gallery, state));
  }

  function disconnect(section) {
    const state = sections.get(section);
    if (!state) return;
    state.observer.disconnect();
    state.cleanup.forEach((cleanup) => cleanup());
    state.animations.forEach((animation) => animation.cancel());
    sections.delete(section);
  }

  function start() {
    if (reducedMotion.matches) return;
    document.querySelectorAll('#MainContent > .shopify-section').forEach(connect);
  }

  function stop() {
    Array.from(sections.keys()).forEach(disconnect);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element && event.target.closest('#MainContent')) connect(event.target);
  }, { signal: listeners.signal });

  document.addEventListener('shopify:section:unload', (event) => disconnect(event.target), { signal: listeners.signal });

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open || !details.closest('#MainContent .accordion')) return;
    const content = details.querySelector(':scope > .accordion__content');
    const state = sections.get(details.closest('.shopify-section'));
    if (!content || !state) return;
    animate(content, state, [
      { opacity: 0.84, transform: 'translateY(4px)' },
      { opacity: 1, transform: 'translateY(0)' },
    ], { duration: 400 });
  }, { capture: true, signal: listeners.signal });

  reducedMotion.addEventListener('change', () => {
    stop();
    start();
  }, { signal: listeners.signal });

  window.addEventListener('pagehide', stop, { signal: listeners.signal });
  window.addEventListener('pageshow', start, { signal: listeners.signal });
  window.SnuglerPolish = {
    destroy() {
      stop();
      listeners.abort();
      delete window.SnuglerPolish;
    },
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true, signal: listeners.signal });
  else start();
})();
